import { useEffect, useState } from "react";
import { Search as SearchIcon, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../../components/ui/switch";
import { useApi } from "../../hooks/useApi";
import { api, web, pay, errMsg, toList } from "../../lib/api";
import { PageHeader, Panel, Loader, ErrorNote, Empty, shortDate } from "../../components/app/kit";
import { CompLog } from "../../components/app/CompLog";
import { InviteWebsite } from "../../components/app/InviteWebsite";
import { AccountGrants, TAGS, TagFilter, tagLabel } from "../../components/app/AccountGrants";

const NotLive = () => (
  <Panel data-testid="admin-not-live">
    <div className="flex items-start gap-4">
      <ShieldCheck className="mt-1 h-6 w-6 shrink-0 text-amber-300" />
      <div className="space-y-3 text-sm text-slate-300">
        <p className="font-display text-2xl font-bold uppercase text-white">Admin endpoints aren't live yet</p>
        <p>This page is ready, but the GAMEDAY mobile backend doesn't have the admin endpoints yet. Once they're added and published, team search and the free-access switches appear here automatically.</p>
        <ul className="list-disc space-y-1 pl-5 font-mono-gd text-xs text-slate-400">
          <li>GET /api/admin/teams?q= → [{"{"}id, name, owner_name, owner_email, subscription_status, comp_access, comp_granted_at{"}"}]</li>
          <li>POST /api/admin/teams/{"{team_id}"}/comp-access {"{"}enabled{"}"} → updated team</li>
        </ul>
      </div>
    </div>
  </Panel>
);

const TeamRow = ({ t, onToggle, onReset, onTag, busy }) => (
  <li data-testid={`admin-team-${t.id}`} className="flex flex-col gap-3 rounded-2xl border border-[#1E293B] bg-[#0F172A] px-5 py-4 sm:flex-row sm:items-center">
    <div className="min-w-0 flex-1">
      <p className="flex items-center gap-2 truncate font-display text-xl font-bold uppercase text-white">{t.name}{t.tag && <span data-testid={`admin-team-tag-badge-${t.id}`} className="rounded-full bg-[#39FF14]/15 px-2 py-0.5 font-mono-gd text-[10px] uppercase tracking-wider text-[#39FF14]">{tagLabel(t.tag)}</span>}</p>
      <p className="truncate text-sm text-slate-400">{t.owner_name ?? "Owner"}{t.owner_email ? ` · ${t.owner_email}` : ""}</p>
    </div>
    <span className="font-mono-gd text-xs uppercase tracking-wider text-slate-400">
      {t.subscription_status ?? "—"}{t.comp_access && t.comp_granted_at ? ` · since ${shortDate(t.comp_granted_at)}` : ""}
    </span>
    <select data-testid={`admin-team-tag-select-${t.id}`} aria-label="Reason" value={t.tag ?? ""} disabled={busy} onChange={(e) => onTag(t, e.target.value || null)}
      className="self-start rounded-full border border-[#1E293B] bg-[#0A0E1A] px-3 py-1.5 text-xs font-semibold text-slate-200 outline-none focus:border-[#39FF14] sm:self-auto">
      <option value="">No reason</option>
      {TAGS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
    </select>
    <label className="flex items-center gap-3">
      <span className={`font-mono-gd text-xs uppercase tracking-wider ${t.comp_access ? "text-[#39FF14]" : "text-slate-400"}`}>{t.comp_access ? "Free access" : "Paid / off"}</span>
      <Switch data-testid={`admin-comp-switch-${t.id}`} checked={!!t.comp_access} disabled={busy} onCheckedChange={(v) => onToggle(t, v)} className="data-[state=checked]:bg-[#39FF14]" />
    </label>
    {!t.comp_access && t.subscription_status && t.subscription_status !== "inactive" && (
      <button data-testid={`admin-reset-${t.id}`} disabled={busy} onClick={() => onReset(t)} className="rounded-full border border-[#1E293B] px-3 py-1.5 text-xs font-semibold text-slate-300 hover:border-[#FF3B30]/50 hover:text-red-200 disabled:opacity-50">Reset to inactive</button>
    )}
  </li>
);

export default function FreeAccess() {
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [overrides, setOverrides] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const t = setTimeout(() => setTerm(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data, loading, error } = useApi(`/admin/teams?q=${encodeURIComponent(term)}`, api);
  const log = useApi("/admin/comp-log", web);
  const tagsApi = useApi("/admin/team-tags", web);
  const everyTeam = useApi(!["all", "none"].includes(filter) && "/admin/teams?q=", api);
  const tagDocs = Array.isArray(tagsApi.data) ? tagsApi.data : [];
  const tagOf = Object.fromEntries(tagDocs.map((d) => [d.team_id, d.tag]));
  const notLive = error && /not found/i.test(error);
  const needle = term.toLowerCase();
  const matches = (t) => !needle || [t.name, t.owner_name, t.owner_email].some((v) => v?.toLowerCase().includes(needle));
  const withTag = (t) => ({ ...t, ...overrides[t.id], tag: tagOf[t.id] ?? null });
  const searched = toList(data, ["teams", "items"]).filter(matches).map(withTag);
  const allById = Object.fromEntries(toList(everyTeam.data, ["teams", "items"]).map((t) => [t.id, t]));
  const tagged = (k) => tagDocs.filter((d) => d.tag === k)
    .map((d) => withTag(allById[d.team_id] ?? { id: d.team_id, name: d.team_name, owner_name: d.owner_name, owner_email: d.owner_email }))
    .filter(matches);
  const teams = filter === "all" ? searched : filter === "none" ? searched.filter((t) => !t.tag) : tagged(filter);
  const count = (k) => (k === "all" ? searched.length : k === "none" ? searched.filter((t) => !t.tag).length : tagged(k).length);

  const setTag = async (t, tag) => {
    setBusyId(t.id);
    try {
      await web.post(`/admin/team-tags/${t.id}`, { tag, team_name: t.name ?? null, owner_name: t.owner_name ?? null, owner_email: t.owner_email ?? null });
      tagsApi.reload();
      toast.success("Reason saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };

  const reset = async (t) => {
    if (!window.confirm(`Reset ${t.name} to inactive? They lose access until they subscribe again.`)) return;
    setBusyId(t.id);
    try {
      const { data: res } = await pay.post(`/admin/teams/${t.id}/reset`);
      setOverrides((o) => ({ ...o, [t.id]: { ...o[t.id], subscription_status: res.subscription_status } }));
      toast.success(`${t.name} reset to inactive`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };

  const toggle = async (t, enabled) => {
    setBusyId(t.id);
    try {
      const { data: res } = await web.post(`/admin/teams/${t.id}/comp-access`, { enabled, team_name: t.name, owner_name: t.owner_name ?? null, owner_email: t.owner_email ?? null });
      log.reload();
      setOverrides((o) => ({ ...o, [t.id]: { comp_access: res?.comp_access ?? enabled, subscription_status: res?.subscription_status ?? t.subscription_status, comp_granted_at: res?.comp_granted_at ?? t.comp_granted_at } }));
      toast.success(enabled ? `Free access on for ${t.name}` : `Free access off for ${t.name}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Admin" title="Free access" />
      <InviteWebsite />
      <p className="-mt-4 mb-8 max-w-2xl text-slate-400">Give free access to people by email, or search any GAMEDAY team and switch complimentary access on or off. Changes apply in the mobile app too.</p>
      <AccountGrants onChanged={log.reload} />
      <h2 className="mb-4 font-display text-2xl font-bold uppercase text-white">Teams</h2>
      <label className="no-print mb-6 flex items-center gap-3 rounded-full border border-[#1E293B] bg-[#0F172A] px-5 py-3.5 focus-within:border-[#39FF14]">
        <SearchIcon className="h-5 w-5 text-[#39FF14]" />
        <input data-testid="admin-team-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search team, coach name or email" className="w-full bg-transparent text-white outline-none placeholder:text-slate-500" />
      </label>
      {!loading && !notLive && !error && <TagFilter count={count} value={filter} onChange={setFilter} prefix="team-filter" />}
      {loading ? <Loader label="Searching teams" /> : notLive ? <NotLive /> : error ? <ErrorNote error={error} /> : !teams.length ? <Empty title={filter === "all" ? "No teams match" : "No teams with this reason"} /> : (
        <ul className="space-y-3">{teams.map((t) => <TeamRow key={t.id} t={t} onToggle={toggle} onReset={reset} onTag={setTag} busy={busyId === t.id} />)}</ul>
      )}
      <CompLog entries={log.data} loading={log.loading} error={log.error} />
    </div>
  );
}
